import { CREATE_PARAM, ReportRoute } from 'Routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { generateStore } from 'utilities/reportStoreFactory';
import { persister } from 'index';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useReportId } from 'hooks/helperHooks';
import { useShallow } from 'zustand/react/shallow';

// Most of initial state management happens in this file, so it is important to keep caution when modifying it.

const useGetReportData = <ConfigData, ReportContentConfig, Report extends GenericReport<ConfigData, ReportContentConfig>>(
  useReportStore: ReturnType<typeof generateStore>,
  getReportFn: (requestBody: GetData<ReportContentConfig>, reportId: string) => Promise<Report>,
  setReportMetadata: (data: Report) => {
    newSearchChips: unknown;
    newSpecialConfigOptions: unknown;
  },
  getReportQueryKey: string
) => {
  const { reportId } = useReportId();

  const actions = useReportStore((state) => state.actions);
  const { requestBody, canGetNewData } = useReportStore(
    useShallow((state) => ({
      requestBody: state.selectedConfgValues,
      canGetNewData: state.canGetNewData,
    }))
  );

  // this needs to be a query (not a mutation), so that it is possible to use setQueryData and cache the result locally
  // hence canGetNewData must exist
  const query = useQuery({
    queryKey: [getReportQueryKey, requestBody, reportId],
    queryFn: reportId ? () => getReportFn(requestBody, reportId) : undefined, // safe, since `enabled: !!reportId` is set below
    enabled: !!reportId && reportId !== CREATE_PARAM && canGetNewData,
  });

  useEffect(() => {
    if (query.data && canGetNewData) {
      const { configData, chartData } = query.data;
      const { newSearchChips, newSpecialConfigOptions } = setReportMetadata(query.data);
      actions.setSearchChips(newSearchChips);
      actions.setSpecialConfigOptions(newSpecialConfigOptions);
      actions.setLayout(configData);
      actions.setChartData(chartData);
      actions.setCanGetNewData(false);
    }
  }, [query.data, canGetNewData, actions, setReportMetadata]);

  return query;
};

const useGenerateReport = <
  ConfigData,
  ReportContentConfig,
  ReportGenerationCriteria extends GenericReportGenerationCriteria<ConfigData, ReportContentConfig>,
  Report extends GenericReport<ConfigData, ReportContentConfig>,
>(
  useReportStore: ReturnType<typeof generateStore>,
  reportRoute: ReportRoute,
  saveReportFn: (requestBody: ReportGenerationCriteria) => Promise<Report>,
  getReportQueryKey: string
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const actions = useReportStore((state) => state.actions);

  return useMutation({
    mutationFn: ({ requestBody }: { requestBody: ReportGenerationCriteria }) => {
      return saveReportFn(requestBody);
    },
    onSuccess: (data) => {
      const { reportId, reportContentConfig } = data;
      const queryKeyCacheUpdate = [getReportQueryKey, reportContentConfig, reportId];
      queryClient.setQueryData(queryKeyCacheUpdate, data);
      persister.persistQueryByKey(queryKeyCacheUpdate, queryClient);
      queryClient.invalidateQueries({ queryKey: ['getDashboardReports'] });

      if (reportRoute) {
        navigate(`${reportRoute}/${reportId}`, { replace: true });
      }

      actions.setCanGetNewData(true);
    },
  });
};

const useUpdateReportSetup = <ConfigData, ReportContentConfig, Report extends GenericReport<ConfigData, ReportContentConfig>>(
  useReportStore: ReturnType<typeof generateStore>,
  updateReportSetupFn: (requestBody: UpdateSetup<ConfigData, ReportContentConfig>, reportId: string) => Promise<Report>,
  getReportQueryKey: string
) => {
  const { reportId } = useReportId();
  const queryClient = useQueryClient();
  const actions = useReportStore((state) => state.actions);

  return useMutation({
    mutationFn: ({ requestBody }: { requestBody: UpdateSetup<ConfigData, ReportContentConfig> }) => {
      return updateReportSetupFn(requestBody, reportId ?? ''); // this is called only when reportId is defined
    },
    onMutate: () => actions.setCanGetNewData(false),
    onSuccess: (data) => {
      const { reportId, reportContentConfig } = data;
      const queryKeyCacheUpdate = [getReportQueryKey, reportContentConfig, reportId];
      queryClient.setQueryData(queryKeyCacheUpdate, data);
      persister.persistQueryByKey(queryKeyCacheUpdate, queryClient);
      actions.setCanGetNewData(true);
    },
  });
};

const useGetOrgLayout = <ConfigData, ReportContentConfig>(
  useReportStore: ReturnType<typeof generateStore>,
  getLayoutFn: () => Promise<Layout<ConfigData, ReportContentConfig>>,
  getLayoutQueryKey: string
) => {
  const actions = useReportStore((state) => state.actions);
  const layout = useReportStore((state) => state.layout);

  const query = useQuery({
    queryKey: [getLayoutQueryKey],
    queryFn: () => getLayoutFn(),
  });

  useEffect(() => {
    if (query.data && !layout) {
      actions.setLayout(query.data.configData);
    }
  }, [query.data, layout, actions]);

  return query;
};

const useSaveOrgLayout = <ConfigData, ReportContentConfig>(
  saveLayoutFn: (requestBody: UpdateLayout<ConfigData>) => Promise<UpdateLayout<ConfigData>>,
  getLayoutQueryKey: string
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestBody }: { requestBody: UpdateLayout<ConfigData> }) => {
      return saveLayoutFn(requestBody);
    },
    onMutate: ({ requestBody }) => {
      const layout: Layout<ConfigData, ReportContentConfig> | undefined = queryClient.getQueryData([getLayoutQueryKey]);
      if (layout) {
        queryClient.setQueryData([getLayoutQueryKey], { ...layout, ...requestBody });
        persister.persistQueryByKey([getLayoutQueryKey], queryClient);
      }
    },
  });
};

type Layout<ConfigData, ReportContentConfig> = { configData: ConfigData; reportContentConfig: ReportContentConfig };
type UpdateLayout<ConfigData> = { configData: ConfigData };
type GenericReportGenerationCriteria<ConfigData, ReportContentConfig> = {
  configData: ConfigData;
  reportContentConfig: ReportContentConfig;
  [k: string]: unknown;
};
type GenericReport<ConfigData, ReportContentConfig> = {
  reportId: string;
  configData: ConfigData;
  reportContentConfig: ReportContentConfig;
  [k: string]: unknown;
};
type UpdateSetup<ConfigData, ReportContentConfig> = { configData: ConfigData; reportContentConfig: ReportContentConfig };
type GetData<ReportContentConfig> = ReportContentConfig;

type ReportQueriesProps<
  ConfigData,
  ReportContentConfig,
  ReportGenerationCriteria extends GenericReportGenerationCriteria<ConfigData, ReportContentConfig>,
  Report extends GenericReport<ConfigData, ReportContentConfig>,
> = {
  reportRoute: ReportRoute;
  useReportStore: ReturnType<typeof generateStore>;
  getOrgLayoutFn: () => Promise<Layout<ConfigData, ReportContentConfig>>;
  saveOrgLayoutFn: (requestBody: UpdateLayout<ConfigData>) => Promise<UpdateLayout<ConfigData>>;
  generateReportFn: (requestBody: ReportGenerationCriteria) => Promise<Report>;
  getReportDataFn: (requestBody: GetData<ReportContentConfig>, reportId: string) => Promise<Report>;
  updateReportSetupFn: (requestBody: UpdateSetup<ConfigData, ReportContentConfig>, reportId: string) => Promise<Report>;
  setReportMetadata: (data: Report) => {
    newSearchChips: unknown;
    newSpecialConfigOptions: unknown;
  };
};

export const useReportQueries = <
  ConfigData,
  ReportContentConfig,
  ReportGenerationCriteria extends GenericReportGenerationCriteria<ConfigData, ReportContentConfig>,
  Report extends GenericReport<ConfigData, ReportContentConfig>,
>({
  reportRoute,
  useReportStore,
  getOrgLayoutFn,
  saveOrgLayoutFn,
  generateReportFn,
  getReportDataFn,
  updateReportSetupFn,
  setReportMetadata,
}: ReportQueriesProps<ConfigData, ReportContentConfig, ReportGenerationCriteria, Report>) => {
  // The `data` is not exposed on purpose – it must be accessed only through the store
  const getReportQueryKey = reportRoute + 'getReport';
  const getLayoutQueryKey = reportRoute + 'getLayout';
  useGetOrgLayout(useReportStore, getOrgLayoutFn, getLayoutQueryKey);
  const { mutate: saveOrgLayout } = useSaveOrgLayout(saveOrgLayoutFn, getLayoutQueryKey);
  const { mutate: generateReportMutation, isPending: isGeneratingReport } = useGenerateReport(
    useReportStore,
    reportRoute,
    generateReportFn,
    getReportQueryKey
  );
  const { mutate: updateReportSetupMutation, isPending: isUpdatingReport } = useUpdateReportSetup(
    useReportStore,
    updateReportSetupFn,
    getReportQueryKey
  );
  const { isLoading: isLoadingExistingReport } = useGetReportData(useReportStore, getReportDataFn, setReportMetadata, getReportQueryKey);

  return {
    isLoadingReport: isGeneratingReport || isUpdatingReport || isLoadingExistingReport,
    saveOrgLayout,
    generateReportMutation,
    updateReportSetupMutation,
  };
};
